/**
 * Wertia Dossier — Grafici visualizzazione coperture svizzere (stile SVAG).
 *
 * Espone:
 *   - TimelineInvaliditaSVAG → ComposedChart Recharts: timeline temporale
 *                              (Tag 1 → 730 → Età 65) con area impilata copertura
 *                              + linea oro "Heutiges Einkommen" + linea nera
 *                              tratteggiata "Vorsorgebedarf" + area rossa "Lücke".
 *   - TimelineDecessoSVAG   → ComposedChart Recharts: timeline copertura per
 *                              superstiti dal momento del decesso fino all'età 65.
 *   - BarometerGauge        → Gauge SVG semicircolare con gradiente rosso→arancio→verde.
 *   - DonutDistribuzione    → Donut chart Recharts per ripartizione del netto annuo.
 *   - SchemaSistemaSvizzero → infografica dei 3 pilastri.
 *   - ComparativaScaleFiscali → mini bar chart fiscali ZH/BE/LU.
 * ======================================================================== */
import React from "react";
import {
  ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ReferenceLine, ResponsiveContainer,
  PieChart, Pie, Cell,
} from "recharts";
import { fmtCHF } from "./utils";
import { CHART_COLOR, CHART_AXIS } from "@/lib/chartPalette";

/* ===========================================================================
 * Costanti CH 2026
 * ======================================================================== */
const UVG_MAX_SALARIO = 148200;       // salario massimo assicurato UVG/anno
const AVS_RENDITA_MAX = 30240;        // rendita massima AVS Scala 44 (singolo)
// Palette UFFICIALE Wertia (vedi /lib/chartPalette.js)
const COLOR_GOLD = CHART_COLOR.GOLD;            // brand Wertia gold (#D7A93F) — linea "Heutiges Einkommen"
const COLOR_INK = CHART_COLOR.COCOA;            // brand cocoa (#2E2622) — linea Vorsorgebedarf
const COLOR_GAP = CHART_COLOR.ERROR;            // rosso lacuna (#FF5757)
const COLOR_AZIENDA = CHART_COLOR.STONE;        // stone (#433E3D) datore lavoro
const COLOR_FASE2_INF = CHART_COLOR.INFO;       // blu (#2E9EDF) — UVG complementare
const COLOR_FASE2_MAL = CHART_COLOR.STONE;      // stone (#433E3D) — KTG (alternato per leggibilità stack)
const COLOR_AVS = CHART_COLOR.GOLD_DARK;        // gold scuro (#B2872D) — 1° pilastro
const COLOR_LPP = CHART_COLOR.SUCCESS;          // verde (#389D6D) — 2° pilastro

/* ===========================================================================
 * Helper di formattazione
 * ======================================================================== */
const fmtCHFShort = (v) => {
  const n = Number(v) || 0;
  if (Math.abs(n) >= 1000) return `${(n / 1000).toLocaleString("de-CH", { minimumFractionDigits: 0, maximumFractionDigits: 1 })}k`;
  return n.toLocaleString("de-CH", { maximumFractionDigits: 0 });
};

/** Tooltip custom: mostra ogni voce con colore + somma + lacuna evidenziata.
 *  Riceve via `payload[0].payload._mode` ("infortunio" o "malattia") per usare
 *  label tecnici corretti (UVG-Komplementär vs BVG/LPP-Invalidenrente). */
const SVAGTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  const get = (k) => payload.find((p) => p.dataKey === k)?.value || 0;
  const xlabel = payload[0]?.payload?.xlabel || label;
  const mode = payload[0]?.payload?._mode || "infortunio";
  const isInf = mode === "infortunio";

  const fase1 = get("fase1");
  const fase2 = get("fase2");
  const avs = get("avs");
  const lpp = get("lpp");
  const lucke = get("lucke");
  const heutiges = get("heutiges");
  const vorsorgebedarf = get("vorsorgebedarf");
  const copertura = fase1 + fase2 + avs + lpp;

  return (
    <div className="bg-white border border-stone-300 rounded shadow-lg px-3 py-2 text-xs space-y-1 min-w-[260px]">
      <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold border-b pb-1 mb-1">{xlabel}</div>
      {fase1 > 0 && (
        <div className="flex justify-between gap-3"><span className="text-stone-700">Arbeitgeber / Datore di lavoro</span><span className="font-mono font-semibold">{fmtCHF(fase1)}</span></div>
      )}
      {fase2 > 0 && (
        <div className="flex justify-between gap-3"><span className="text-stone-700">{isInf ? "UVG-Taggeld 80%" : "KTG 80%"}</span><span className="font-mono font-semibold">{fmtCHF(fase2)}</span></div>
      )}
      {avs > 0 && (
        <div className="flex justify-between gap-3"><span className="text-stone-700">IV-Rente 1. Säule (AVS/IV)</span><span className="font-mono font-semibold">{fmtCHF(avs)}</span></div>
      )}
      {lpp > 0 && (
        <div className="flex justify-between gap-3"><span className="text-stone-700">{isInf ? "UVG-Komplementärrente" : "BVG/LPP-Invalidenrente"}</span><span className="font-mono font-semibold">{fmtCHF(lpp)}</span></div>
      )}
      <div className="flex justify-between gap-3 border-t pt-1 mt-1 font-semibold text-stone-900">
        <span>Gesamtleistung / Copertura totale</span><span className="font-mono">{fmtCHF(copertura)}</span>
      </div>
      {vorsorgebedarf > 0 && (
        <div className="flex justify-between gap-3 text-stone-700">
          <span>Vorsorgebedarf (80% Netto)</span><span className="font-mono">{fmtCHF(vorsorgebedarf)}</span>
        </div>
      )}
      {heutiges > 0 && (
        <div className="flex justify-between gap-3 text-stone-600">
          <span>Heutiges Einkommen</span><span className="font-mono" style={{ color: COLOR_GOLD }}>{fmtCHF(heutiges)}</span>
        </div>
      )}
      {lucke > 0 && (
        <div className="flex justify-between gap-3 bg-red-50 -mx-3 -mb-2 px-3 py-1.5 mt-1 border-t border-red-200 font-bold text-red-700">
          <span>▼ Lücke / Lacuna</span><span className="font-mono">− {fmtCHF(lucke)}</span>
        </div>
      )}
    </div>
  );
};

/* ===========================================================================
 * TIMELINE INVALIDITÀ — stile SVAG
 *
 * Asse X temporale (categoriale, equispaziato):
 *   Infortunio: Tag 1 · Tag 3 · Tag 4 · Tag 730 · Tag 731 · Età 65
 *   Malattia:   Tag 1 · Tag 21 · Tag 22 · Tag 720 · Tag 721 · Età 65
 *
 * Aree stacked:
 *   - "Azienda" (100% sal) → solo fase 1
 *   - "UVG / KTG" (80% sal) → solo fase 2
 *   - "AVS Scala 44" + "LPP/UVG" → solo fase 3 (rendita IV)
 *   - "Lücke" (rosso) → stacked sopra la copertura fino al Vorsorgebedarf
 *
 * Linee:
 *   - Heutiges Einkommen (oro #c9a14a, solida)
 *   - Vorsorgebedarf    (nera tratteggiata)
 * ======================================================================== */
export function TimelineInvaliditaSVAG({
  salarioLordo,
  salarioUvgAssicurato,
  isInfortunio = true,
  fabbisognoAnnuo,        // optional: fornito dal backend
  coperturaFase3Annua,    // optional: totale reale rendita IV permanente (AVS + LPP/UVG)
  height = 320,
}) {
  const sal = Number(salarioLordo) || 0;
  const salUVG = Number(salarioUvgAssicurato) || Math.min(sal, UVG_MAX_SALARIO);

  // Vorsorgebedarf (target di copertura):
  // - Se il backend ce lo fornisce (anche 0 esplicito → fabbisogno NULLO), lo rispettiamo.
  // - Altrimenti calcoliamo: 90% (infortunio) o 80% (malattia) del salario.
  const targetCalcolato = isInfortunio ? salUVG * 0.90 : sal * 0.80;
  const target = (fabbisognoAnnuo === undefined || fabbisognoAnnuo === null)
    ? targetCalcolato
    : Number(fabbisognoAnnuo);

  // Fase 3 (rendita IV): se il backend ci passa il totale reale, lo usiamo.
  // Altrimenti calcolo client-side approssimativo (90% sal UVG per infortunio, 50% sal per malattia).
  const fase3TotCalcolato = isInfortunio ? salUVG * 0.90 : sal * 0.50;
  const fase3Tot = (coperturaFase3Annua === undefined || coperturaFase3Annua === null)
    ? fase3TotCalcolato
    : Number(coperturaFase3Annua);
  const avsAnnuo = Math.min(AVS_RENDITA_MAX, fase3Tot);
  const lppAnnuo = Math.max(0, fase3Tot - avsAnnuo);

  // Costanti timeline
  const f1End = isInfortunio ? 3 : 21;
  const f2Start = f1End + 1;
  const f2End = isInfortunio ? 730 : 720;
  const f3Start = f2End + 1;

  // Costruzione datapoints con asse X numerico in scala proporzionale:
  //   Fase 1 (Tag 1-3)        → 0% → 10%  larghezza grafico
  //   Fase 2 (Tag 4-730/720)  → 10% → 65% larghezza (la più lunga, blu/viola)
  //   Fase 3 (Tag 731+ → 65)  → 65% → 100% larghezza
  // I valori di fase 2 e 3 partono +0.1 dal precedente per disegnare lo "step"
  // verticale senza creare un buco visibile.
  const X_F1_START = 0;       // Tag 1
  const X_F1_END   = 10;      // Tag 3 (fine fase 1)
  const X_F2_START = 10.1;    // Tag 4 (a ridosso di Tag 3 — niente gap visibile)
  const X_F2_END   = 65;      // Tag 730/720 (fine fase 2)
  const X_F3_START = 65.1;    // Tag 731/721 (a ridosso di Tag 730)
  const X_F3_END   = 100;     // Età 65

  const mkRow = (xVal, fase1, fase2, avs, lpp) => {
    const copertura = fase1 + fase2 + avs + lpp;
    const lucke = Math.max(0, target - copertura);
    return {
      x: xVal,
      xlabel: { [X_F1_START]: "Tag 1", [X_F1_END]: `Tag ${f1End}`, [X_F2_START]: `Tag ${f2Start}`, [X_F2_END]: `Tag ${f2End}`, [X_F3_START]: `Tag ${f3Start}`, [X_F3_END]: "Età 65" }[xVal] || "",
      _mode: isInfortunio ? "infortunio" : "malattia",
      fase1, fase2, avs, lpp, lucke,
      heutiges: sal,
      vorsorgebedarf: target,
    };
  };

  const data = [
    mkRow(X_F1_START, sal, 0, 0, 0),                            // Tag 1
    mkRow(X_F1_END,   sal, 0, 0, 0),                            // Tag 3
    mkRow(X_F2_START, 0, 0.80 * salUVG, 0, 0),                  // Tag 4
    mkRow(X_F2_END,   0, 0.80 * salUVG, 0, 0),                  // Tag 730
    mkRow(X_F3_START, 0, 0, avsAnnuo, lppAnnuo),                // Tag 731
    mkRow(X_F3_END,   0, 0, avsAnnuo, lppAnnuo),                // Età 65
  ];

  // Tick custom — mostriamo solo i 4 punti chiave per evitare sovrapposizione di label
  // (Tag 4 e Tag 731 sono impliciti, immediatamente dopo Tag 3 e Tag 730).
  const xTicks = [X_F1_START, X_F1_END, X_F2_END, X_F3_END];
  const xLabels = {
    [X_F1_START]: "Tag 1",
    [X_F1_END]:   `Tag ${f1End}`,
    [X_F2_END]:   `Tag ${f2End}`,
    [X_F3_END]:   "Età 65",
  };

  // Range Y: massimo tra salario, fabbisogno e copertura totale
  const yMax = Math.max(sal, target) * 1.10;

  const colorFase2 = isInfortunio ? COLOR_FASE2_INF : COLOR_FASE2_MAL;
  const labelFase2 = isInfortunio ? "UVG-Taggeld 80%" : "KTG-Taggeld 80%";
  const labelFase3 = isInfortunio ? "UVG-Komplementärrente" : "BVG/LPP-Invalidenrente";

  return (
    <div className="bg-white rounded border border-stone-200 p-3" data-testid={`timeline-svag-${isInfortunio ? "infortunio" : "malattia"}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">
          Timeline copertura — {isInfortunio ? "Unfall / Infortunio" : "Krankheit / Malattia"}
        </div>
        <div className="flex items-center gap-3 text-[10px]">
          <span className="flex items-center gap-1"><span className="w-3 h-0.5" style={{ backgroundColor: COLOR_GOLD, height: "3px" }}></span><span className="text-stone-600">Heutiges Einkommen</span></span>
          <span className="flex items-center gap-1"><span className="w-3 h-0 border-t-2 border-dashed border-stone-900"></span><span className="text-stone-600">Vorsorgebedarf</span></span>
        </div>
      </div>

      <div style={{ width: "100%", height }}>
        <ResponsiveContainer>
          <ComposedChart data={data} margin={{ top: 10, right: 28, left: 0, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={CHART_AXIS.grid} />
            <XAxis
              dataKey="x"
              type="number"
              domain={[X_F1_START, X_F3_END]}
              ticks={xTicks}
              tickFormatter={(v) => xLabels[v] || ""}
              tick={{ fontSize: 10, fill: CHART_AXIS.tick }}
              axisLine={{ stroke: CHART_AXIS.axis }}
              tickLine={{ stroke: CHART_AXIS.axis }}
              interval={0}
              allowDuplicatedCategory={false}
            />
            <YAxis
              type="number"
              domain={[0, yMax]}
              tickFormatter={(v) => `${fmtCHFShort(v)}`}
              tick={{ fontSize: 10, fill: CHART_AXIS.tick }}
              axisLine={{ stroke: CHART_AXIS.axis }}
              tickLine={{ stroke: CHART_AXIS.axis }}
              width={56}
            />
            <Tooltip content={<SVAGTooltip />} cursor={{ stroke: CHART_AXIS.cursor, strokeWidth: 1, strokeDasharray: "3 3" }} />

            {/* Aree stacked — copertura per fase (step) */}
            <Area
              type="stepAfter"
              dataKey="fase1"
              stackId="cov"
              stroke={COLOR_AZIENDA}
              fill={COLOR_AZIENDA}
              fillOpacity={0.85}
              name="Arbeitgeber (100%)"
              isAnimationActive={false}
            />
            <Area
              type="stepAfter"
              dataKey="fase2"
              stackId="cov"
              stroke={colorFase2}
              fill={colorFase2}
              fillOpacity={0.70}
              name={labelFase2}
              isAnimationActive={false}
            />
            <Area
              type="stepAfter"
              dataKey="avs"
              stackId="cov"
              stroke={COLOR_AVS}
              fill={COLOR_AVS}
              fillOpacity={0.75}
              name="IV-Rente 1. Säule"
              isAnimationActive={false}
            />
            <Area
              type="stepAfter"
              dataKey="lpp"
              stackId="cov"
              stroke={COLOR_LPP}
              fill={COLOR_LPP}
              fillOpacity={0.70}
              name={labelFase3}
              isAnimationActive={false}
            />
            <Area
              type="stepAfter"
              dataKey="lucke"
              stackId="cov"
              stroke={COLOR_GAP}
              fill={COLOR_GAP}
              fillOpacity={0.35}
              strokeWidth={1.5}
              name="Lücke (lacuna)"
              isAnimationActive={false}
            />

            {/* Linea oro = Heutiges Einkommen (salario attuale) */}
            <Line
              type="stepAfter"
              dataKey="heutiges"
              stroke={COLOR_GOLD}
              strokeWidth={3}
              dot={false}
              name="Heutiges Einkommen"
              isAnimationActive={false}
            />
            {/* Linea nera tratteggiata = Vorsorgebedarf (solo se target > 0) */}
            {target > 0 && (
              <Line
                type="stepAfter"
                dataKey="vorsorgebedarf"
                stroke={COLOR_INK}
                strokeWidth={2}
                strokeDasharray="6 4"
                dot={false}
                name="Vorsorgebedarf"
                isAnimationActive={false}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Legend compatta — terminologia tecnica differenziata Infortunio vs Malattia */}
      <div className="flex flex-wrap gap-3 text-[10px] mt-2 pt-2 border-t border-stone-100">
        <LegendItem color={COLOR_AZIENDA} label="Arbeitgeber" />
        <LegendItem color={colorFase2} label={labelFase2} />
        <LegendItem color={COLOR_AVS} label="IV-Rente 1. Säule" />
        <LegendItem color={COLOR_LPP} label={labelFase3} />
        <LegendItem color={COLOR_GAP} label="Lücke" />
      </div>

      {/* Descrizione tecnica + ipotesi calcolo */}
      <p className="text-[10px] text-stone-600 mt-2 leading-snug">
        {isInfortunio
          ? <><span className="font-semibold">Unfall → Invalidität:</span> Kurzfristig UVG-Taggeld 80% nach gesetzlicher Wartefrist. Langfristig IV-Rente + UVG-Komplementärrente bis max. 90% des versicherten Verdienstes (UVG art. 20).</>
          : <><span className="font-semibold">Krankheit → Invalidität:</span> Kurzfristig Lohnfortzahlung / KTG 80% bis max. 720 Tage, sofern KTG vorhanden. Langfristig IV-Rente + BVG/LPP-Invalidenrente (BVG art. 24). <span className="font-bold">Kein UVG</span>.</>
        }
      </p>
      <p className="text-[9px] text-amber-700 italic mt-1 leading-snug bg-amber-50 border border-amber-200 rounded px-2 py-1">
        ⚠ <span className="font-semibold">Ipotesi di calcolo</span>: invalidità totale / grado AI ≥ 70%. In caso di invalidità parziale le prestazioni vengono ridotte proporzionalmente (sotto 40% nessuna rendita LPP).
        {isInfortunio && <> L'inizio effettivo della rendita dipende dalla decisione AI/IV e dalla chiusura del caso UVG.</>}
      </p>
    </div>
  );
}

/* ===========================================================================
 * TIMELINE DECESSO — stile SVAG
 *
 * Per il decesso le rendite sono costanti dal momento del sinistro fino:
 *   - vedovile fino a 65 anni (o nuove nozze)
 *   - orfani fino a 18 anni (25 se in formazione)
 *
 * Per semplicità grafica: timeline costante "Oggi → +5 anni → +15 anni → Età 65".
 *
 * Voci attese (dal backend `scenari[].voci`):
 *   - "AVS Vedovile", "AVS Orfani", "LPP Vedovile", "LPP Orfani", "UVG Vedovile",
 *     "UVG Orfani", "Capitale 3a/3b", ecc.
 * ======================================================================== */
export function TimelineDecessoSVAG({
  voci = [],
  fabbisognoAnnuo,
  salarioLordo,
  isInfortunio = true,
  height = 280,
}) {
  const sal = Number(salarioLordo) || 0;
  // Rispetto del fabbisogno fornito dal backend (anche se = 0 esplicito).
  const target = (fabbisognoAnnuo === undefined || fabbisognoAnnuo === null)
    ? sal * 0.80
    : Number(fabbisognoAnnuo);

  // Aggrega per categoria (AVS · LPP · UVG · 3a/3b)
  const sumByKey = (re) => voci
    .filter((v) => re.test(v.label || ""))
    .reduce((s, v) => s + (Number(v.importo_chf) || 0), 0);

  const avs = sumByKey(/\bAVS\b/i);
  const lpp = sumByKey(/\bLPP|BVG\b/i);
  const uvg = sumByKey(/\bUVG|LAINF\b/i);
  const terzo = sumByKey(/3a|3b|capitale|terzo/i);

  // Sometimes voci have raw labels without keywords; fallback: use totale_copertura.
  const copTot = avs + lpp + uvg + terzo;
  const lucke = Math.max(0, target - copTot);

  // 4 punti timeline (categoriale)
  const mkRow = (label) => ({
    x: label,
    avs,
    lpp,
    uvg,
    terzo,
    lucke,
    heutiges: sal,
    vorsorgebedarf: target,
  });

  const data = [
    mkRow("Anno 1"),
    mkRow("Anno 5"),
    mkRow("Anno 15"),
    mkRow("Età 65"),
  ];

  const yMax = Math.max(sal, target, copTot) * 1.10;

  return (
    <div className="bg-white rounded border border-stone-200 p-3" data-testid={`timeline-svag-decesso-${isInfortunio ? "infortunio" : "malattia"}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">
          Timeline rendite superstiti — Decesso ({isInfortunio ? "Infortunio" : "Malattia"})
        </div>
        <div className="flex items-center gap-3 text-[10px]">
          <span className="flex items-center gap-1"><span className="w-3" style={{ backgroundColor: COLOR_GOLD, height: "3px" }}></span><span className="text-stone-600">Heutiges Einkommen</span></span>
          <span className="flex items-center gap-1"><span className="w-3 h-0 border-t-2 border-dashed border-stone-900"></span><span className="text-stone-600">Vorsorgebedarf</span></span>
        </div>
      </div>

      <div style={{ width: "100%", height }}>
        <ResponsiveContainer>
          <ComposedChart data={data} margin={{ top: 10, right: 28, left: 0, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={CHART_AXIS.grid} />
            <XAxis
              dataKey="x"
              type="category"
              tick={{ fontSize: 10, fill: CHART_AXIS.tick }}
              axisLine={{ stroke: CHART_AXIS.axis }}
              tickLine={{ stroke: CHART_AXIS.axis }}
              interval={0}
            />
            <YAxis
              type="number"
              domain={[0, yMax]}
              tickFormatter={(v) => `${fmtCHFShort(v)}`}
              tick={{ fontSize: 10, fill: CHART_AXIS.tick }}
              axisLine={{ stroke: CHART_AXIS.axis }}
              tickLine={{ stroke: CHART_AXIS.axis }}
              width={56}
            />
            <Tooltip content={<SVAGTooltip />} cursor={{ stroke: CHART_AXIS.cursor, strokeWidth: 1, strokeDasharray: "3 3" }} />

            <Area type="stepAfter" dataKey="avs"   stackId="cov" stroke={COLOR_AVS}  fill={COLOR_AVS}  fillOpacity={0.75} name="AVS Vedovile/Orfani"   isAnimationActive={false} />
            <Area type="stepAfter" dataKey="lpp"   stackId="cov" stroke={COLOR_LPP}  fill={COLOR_LPP}  fillOpacity={0.70} name="LPP Vedovile/Orfani"   isAnimationActive={false} />
            <Area type="stepAfter" dataKey="uvg"   stackId="cov" stroke={COLOR_FASE2_INF} fill={COLOR_FASE2_INF} fillOpacity={0.70} name="UVG Vedovile/Orfani" isAnimationActive={false} />
            <Area type="stepAfter" dataKey="terzo" stackId="cov" stroke={COLOR_AZIENDA} fill={COLOR_AZIENDA} fillOpacity={0.70} name="3a / 3b / Cap."     isAnimationActive={false} />
            <Area type="stepAfter" dataKey="lucke" stackId="cov" stroke={COLOR_GAP}   fill={COLOR_GAP}   fillOpacity={0.35} strokeWidth={1.5} name="Lücke"  isAnimationActive={false} />

            <Line type="stepAfter" dataKey="heutiges"       stroke={COLOR_GOLD} strokeWidth={3} dot={false} name="Heutiges Einkommen" isAnimationActive={false} />
            {target > 0 && (
              <Line type="stepAfter" dataKey="vorsorgebedarf" stroke={COLOR_INK}  strokeWidth={2} strokeDasharray="6 4" dot={false} name="Vorsorgebedarf" isAnimationActive={false} />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <div className="flex flex-wrap gap-3 text-[10px] mt-2 pt-2 border-t border-stone-100">
        {avs > 0 && <LegendItem color={COLOR_AVS} label="AVS superstiti" />}
        {lpp > 0 && <LegendItem color={COLOR_LPP} label="LPP superstiti" />}
        {uvg > 0 && <LegendItem color={COLOR_FASE2_INF} label="UVG superstiti" />}
        {terzo > 0 && <LegendItem color={COLOR_AZIENDA} label="3a / 3b / capitale" />}
        {lucke > 0 && <LegendItem color={COLOR_GAP} label="Lücke" />}
        {(avs + lpp + uvg + terzo + lucke) === 0 && (
          <span className="text-stone-400 italic">Nessun fabbisogno residuo (cliente senza dipendenti)</span>
        )}
      </div>
    </div>
  );
}

/** Mini legend item */
const LegendItem = ({ color, label }) => (
  <span className="flex items-center gap-1">
    <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: color, opacity: 0.85 }}></span>
    <span className="text-stone-600">{label}</span>
  </span>
);

/* ===========================================================================
 * BAROMETRO GAUGE — Semi-cerchio con gradiente rosso→arancio→verde + lancetta
 *
 * Props:
 *   value  : numero 0-10 (score barometro)
 *   size   : larghezza SVG (default 240)
 *   label  : stringa sotto (es. "SITUAZIONE SOLIDA")
 * ======================================================================== */
export function BarometerGauge({ value = 0, size = 260, label = "" }) {
  const v = Math.max(0, Math.min(10, Number(value) || 0));
  const pct = v / 10;

  // Geometria SVG: semicerchio raggio 100 centrato a (120, 120), spessore 22
  const R = 100;
  const CX = size / 2;
  const CY = size * 0.62;          // centro più in basso per ospitare il valore al centro
  const STROKE = 22;
  const fullLen = Math.PI * R;     // lunghezza arco semicircolare
  const valLen = pct * fullLen;

  // Punta della lancetta
  const angleRad = Math.PI * (1 - pct);   // da π (sinistra) a 0 (destra)
  const handX = CX + (R - STROKE / 2 - 8) * Math.cos(angleRad);
  const handY = CY - (R - STROKE / 2 - 8) * Math.sin(angleRad);

  // Colore lancetta in base allo score
  const handColor = v < 4 ? CHART_COLOR.ERROR : v < 7 ? CHART_COLOR.GOLD : CHART_COLOR.SUCCESS;
  const txtColor = v < 4 ? "text-red-600" : v < 7 ? "text-amber-600" : "text-emerald-600";

  // Stato testuale
  const statoTesto =
    v >= 7 ? "SITUAZIONE SOLIDA"
    : v >= 4 ? "ATTENZIONE — Lacune presenti"
    : "CRITICO — Azioni urgenti";
  const stato = label || statoTesto;

  // Tick marks (0, 2, 4, 6, 8, 10)
  const ticks = [0, 2, 4, 6, 8, 10];

  return (
    <div className="flex flex-col items-center" data-testid="barometer-gauge">
      <svg viewBox={`0 0 ${size} ${size * 0.7}`} width={size} height={size * 0.7}>
        <defs>
          <linearGradient id="gaugeGrad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%"  stopColor={CHART_COLOR.ERROR} />
            <stop offset="50%" stopColor={CHART_COLOR.GOLD} />
            <stop offset="100%" stopColor={CHART_COLOR.SUCCESS} />
          </linearGradient>
          <filter id="needleShadow" x="-50%" y="-50%" width="200%" height="200%">
            <feDropShadow dx="0" dy="2" stdDeviation="2" floodOpacity="0.25" />
          </filter>
        </defs>

        {/* Arco sfondo grigio */}
        <path
          d={`M ${CX - R} ${CY} A ${R} ${R} 0 0 1 ${CX + R} ${CY}`}
          stroke={CHART_AXIS.grid}
          strokeWidth={STROKE}
          fill="none"
          strokeLinecap="round"
        />
        {/* Arco colorato (gradiente) — tagliato al valore tramite dasharray */}
        <path
          d={`M ${CX - R} ${CY} A ${R} ${R} 0 0 1 ${CX + R} ${CY}`}
          stroke="url(#gaugeGrad)"
          strokeWidth={STROKE}
          fill="none"
          strokeLinecap="round"
          strokeDasharray={`${valLen} ${fullLen + 100}`}
        />

        {/* Tick numerici */}
        {ticks.map((t) => {
          const a = Math.PI * (1 - t / 10);
          const tx = CX + (R + 14) * Math.cos(a);
          const ty = CY - (R + 14) * Math.sin(a) + 4;
          return (
            <text key={t} x={tx} y={ty}
              textAnchor="middle"
              className="font-mono"
              fontSize="10"
              fill={CHART_AXIS.tick}>
              {t}
            </text>
          );
        })}

        {/* Lancetta: linea + cerchio centrale */}
        <line
          x1={CX} y1={CY}
          x2={handX} y2={handY}
          stroke={handColor}
          strokeWidth={3}
          strokeLinecap="round"
          filter="url(#needleShadow)"
        />
        <circle cx={CX} cy={CY} r={8} fill={handColor} />
        <circle cx={CX} cy={CY} r={4} fill="#fff" />

        {/* Valore al centro (sopra la lancetta) */}
        <text
          x={CX}
          y={CY - 30}
          textAnchor="middle"
          fontSize="42"
          fontWeight="700"
          className="font-mono"
          fill={handColor}
        >
          {v.toFixed(1)}
        </text>
        <text
          x={CX}
          y={CY - 12}
          textAnchor="middle"
          fontSize="10"
          fill={CHART_AXIS.tick}
          letterSpacing="2"
        >
          /10
        </text>
      </svg>

      <div className={`text-[11px] uppercase tracking-widest font-bold ${txtColor}`}>
        {stato}
      </div>
    </div>
  );
}

/* ===========================================================================
 * DONUT DISTRIBUZIONE — Pie chart Recharts (donut) per ripartizione importi.
 *
 * Props:
 *   data     : [{ label, value, color }]
 *   total    : (opzionale) totale da mostrare al centro
 *   centerLabel  : (opzionale) etichetta sotto al totale
 *   size     : altezza chart in px
 * ======================================================================== */
export function DonutDistribuzione({ data = [], total = 0, centerLabel = "", size = 240 }) {
  const sum = data.reduce((s, d) => s + Math.max(0, Number(d.value) || 0), 0);
  const tot = total || sum;
  if (sum === 0) return <div className="text-xs text-stone-400 italic">Nessun dato disponibile.</div>;

  return (
    <div className="flex flex-col md:flex-row items-center gap-4" data-testid="donut-distribuzione">
      <div className="relative" style={{ width: size, height: size }}>
        <ResponsiveContainer>
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              nameKey="label"
              cx="50%"
              cy="50%"
              innerRadius={size * 0.30}
              outerRadius={size * 0.46}
              paddingAngle={2}
              startAngle={90}
              endAngle={-270}
              isAnimationActive={false}
            >
              {data.map((d, i) => (
                <Cell key={i} fill={d.color} stroke="#fff" strokeWidth={2} />
              ))}
            </Pie>
            <Tooltip
              formatter={(value, name) => [fmtCHF(value), name]}
              contentStyle={{
                backgroundColor: "white",
                border: "1px solid #d6d3d1",
                borderRadius: 6,
                fontSize: 12,
              }}
            />
          </PieChart>
        </ResponsiveContainer>
        {/* Centro: totale */}
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <div className="text-[9px] uppercase tracking-widest text-stone-500 font-semibold">Netto/anno</div>
          <div className="text-lg font-mono font-bold text-stone-900">{fmtCHF(tot)}</div>
          {centerLabel && <div className="text-[10px] text-stone-500 mt-0.5">{centerLabel}</div>}
        </div>
      </div>

      {/* Legenda con valori */}
      <div className="flex-1 space-y-1.5 min-w-[180px]">
        {data.map((d, i) => {
          const pct = sum > 0 ? (d.value / sum) * 100 : 0;
          return (
            <div key={i} className="flex items-center gap-2 text-xs">
              <span className="inline-block w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: d.color }}></span>
              <span className="text-stone-700 truncate">{d.label}</span>
              <span className="ml-auto font-mono text-stone-900 font-semibold whitespace-nowrap">
                {fmtCHF(d.value)}
              </span>
              <span className="text-stone-400 text-[10px] w-10 text-right">{pct.toFixed(0)}%</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ===========================================================================
 * SCHEMA SISTEMA SVIZZERO — i 3 pilastri
 * ======================================================================== */
export function SchemaSistemaSvizzero() {
  const pilastri = [
    {
      n: "1°", titolo: "Pilastro statale", sotto: "AVS / IV / IPG", color: "bg-blue-500",
      kpi_label: "Rendita Scala 44",
      kpi_value: "CHF 15'120 — 30'240",
      desc: "Copertura BASE obbligatoria per tutti i lavoratori in Svizzera. Garantisce la sopravvivenza minima alla pensione.",
      finanz: "Contributi salariali 10.6% (5.3% lavoratore + 5.3% datore)",
    },
    {
      n: "2°", titolo: "Previdenza professionale", sotto: "LPP / BVG + UVG", color: "bg-emerald-500",
      kpi_label: "Tasso conversione",
      kpi_value: "6.80%",
      desc: "OBBLIGATORIO per dipendenti. Salario coordinato (sal. - CHF 26'460). Rendita = capitale × tasso conversione.",
      finanz: "LPP ~15% sal. coord. · UVG ~1.4% lordo (premio NBU)",
    },
    {
      n: "3°", titolo: "Previdenza privata", sotto: "3a vincolata + 3b libera", color: "bg-amber-500",
      kpi_label: "Max 3a / anno",
      kpi_value: "CHF 7'056.00",
      desc: "FACOLTATIVA. Il 3a è DEDUCIBILE fiscalmente. Riscatto a 65 anni o per acquisto prima casa (PPA).",
      finanz: "Volontaria · 3a deducibile fiscalmente · 3b libera",
    },
  ];
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="schema-3-pilastri">
      {pilastri.map((p) => (
        <div key={p.n} className="rounded-lg border-2 border-stone-200 overflow-hidden bg-white shadow-sm">
          <div className={`${p.color} text-white px-4 py-3 flex items-center gap-3`}>
            <div className="text-4xl font-bold leading-none">{p.n}</div>
            <div>
              <div className="font-bold text-base leading-tight">{p.titolo}</div>
              <div className="text-xs opacity-95 mt-0.5">{p.sotto}</div>
            </div>
          </div>
          <div className="p-4 space-y-3">
            <div className="bg-stone-50 rounded p-3 border border-stone-100">
              <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">{p.kpi_label}</div>
              <div className="text-xl font-mono font-bold text-stone-900 mt-1">{p.kpi_value}</div>
            </div>
            <div className="text-sm font-medium text-stone-800 leading-snug">{p.desc}</div>
            <div className="text-[11px] text-stone-500 italic pt-2 border-t border-stone-100">{p.finanz}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ===========================================================================
 * COMPARATIVA SCALE FISCALI ZH / BE / LU
 * ======================================================================== */
export function ComparativaScaleFiscali({ redditoImponibile, currentCantone }) {
  const r = Number(redditoImponibile) || 0;
  const stima = (cantone) => {
    if (r <= 0) return 0;
    if (cantone === "ZH") return r * 0.08;
    if (cantone === "LU") return r * 0.085;
    return r * 0.13;
  };
  const data = [
    { cantone: "Zürich", code: "ZH", aliquota: 0.08, color: CHART_COLOR.INFO },
    { cantone: "Bern", code: "BE", aliquota: 0.13, color: CHART_COLOR.SUCCESS },
    { cantone: "Luzern", code: "LU", aliquota: 0.085, color: CHART_COLOR.GOLD_DARK },
  ];
  const maxStima = Math.max(...data.map((d) => stima(d.code)), 1);

  return (
    <div className="space-y-3" data-testid="comparativa-cantoni">
      <div className="text-xs text-stone-600">
        Reddito imponibile: <span className="font-mono font-semibold">{fmtCHF(r)}</span>
      </div>
      <div className="space-y-2">
        {data.map((d) => {
          const stm = stima(d.code);
          const w = (stm / maxStima) * 100;
          const isCurrent = currentCantone === d.code;
          return (
            <div key={d.code}>
              <div className="flex items-center gap-2 text-xs mb-1">
                <span className={`font-semibold ${isCurrent ? "text-prime" : "text-stone-700"}`}>
                  {d.cantone} ({d.code}){isCurrent ? " ← attuale" : ""}
                </span>
                <span className="ml-auto font-mono font-semibold">{fmtCHF(stm)}</span>
              </div>
              <div className="h-5 bg-stone-100 rounded overflow-hidden border border-stone-200">
                <div
                  style={{ width: `${w}%`, backgroundColor: d.color }}
                  className="h-full flex items-center justify-end pr-2 transition-all"
                >
                  <span className="text-[10px] text-white font-semibold">{(d.aliquota * 100).toFixed(1)}%</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="text-[10px] text-stone-500 italic pt-2 border-t border-stone-100">
        Cantoni non ZH/BE/LU → scala BERN come default (regola Wertia).
      </div>
    </div>
  );
}
